import React, { useState, useCallback, useEffect, useMemo } from 'react';
import logger from '../../utils/logger';
import { Box, Button, Typography, Menu, MenuItem, IconButton, CircularProgress, Alert } from '@mui/material';
import { RestoreFromTrash, Scanner as ScannerIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useVaultItems } from '../../hooks/useVaultItems';
import { getFirestorePath } from '../../utils/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFirestore, doc, updateDoc, deleteDoc, getDoc, collection, addDoc, setDoc } from 'firebase/firestore';
import { getStoragePath, ACCESS_LEVELS, PERMISSIONS, checkPermission, createInitialFolderStructure, STORAGE_FOLDERS } from '../../utils/accessControl';
import FileItem from './FileItem';
import UploadDialog from './UploadDialog';
import DeleteDialog from './DeleteDialog';
import ShareDialog from './ShareDialog';
import MoveDialog from './MoveDialog';
import RecycleBin from './RecycleBin';
import ViewModeToggle from './ViewModeToggle';
import { v4 as uuidv4 } from 'uuid';
import CopyDialog from './CopyDialog';
import { copyFile, moveFile, moveToVault, moveFromVault } from '../../utils/fileSharing';
import { DocumentScannerDialog } from '../DocumentScanner';

const FileExplorer = ({
  title = 'Documents',
  collectionType = STORAGE_FOLDERS.DOCUMENTS,
  storageArea = ACCESS_LEVELS.PERSONAL,
  onFileSelect,
  onFileDelete,
  onFileShare,
  onFileMove,
  onViewModeChange: externalViewModeChange,
  initialViewMode = 'grid',
  initialGridSize = 'medium',
  allowMoveToVault = false,
  allowMoveFromVault = false,
  isPersonal = true
}) => {
  const { currentUser, loading: authLoading } = useAuth();
  const storage = getStorage();
  const db = getFirestore();
  
  // Determine the actual storage area based on props
  const actualStorageArea = isPersonal ? ACCESS_LEVELS.PERSONAL : 
                           storageArea === 'vault' ? ACCESS_LEVELS.VAULT : 
                           ACCESS_LEVELS.FAMILY;

  // State management
  const [currentPath, setCurrentPath] = useState('/');
  const [error, setError] = useState(null);
  const [initializationError, setInitializationError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [gridSize, setGridSize] = useState(initialGridSize);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedForVault, setSelectedForVault] = useState(null);
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [itemToShare, setItemToShare] = useState(null);
  const [itemToMove, setItemToMove] = useState(null);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [recycleBinOpen, setRecycleBinOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [itemToCopy, setItemToCopy] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [shareRecursive, setShareRecursive] = useState(false);
  const [inheritPermissions, setInheritPermissions] = useState(true);
  const [scannerDialogOpen, setScannerDialogOpen] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [operationError, setOperationError] = useState(null);

  // Vault items setup
  const vaultItemsParams = useMemo(() => 
    currentUser ? {
      userId: currentUser.uid,
      familyCode: currentUser.familyCode,
      collectionType,
      currentPath,
      storageArea: actualStorageArea
    } : null
  , [currentUser, collectionType, currentPath, actualStorageArea]);
  
  const { items, loading: itemsLoading, error: vaultError, refresh } = useVaultItems(vaultItemsParams);

  const handleFileSelect = useCallback((file) => {
    if (!currentUser) return;
    if (file.isDirectory) {
      setCurrentPath(file.path || '/');
      return;
    }
    setSelectedFiles(prev => {
      const isSelected = prev.some(f => f.id === file.id);
      if (isSelected) {
        return prev.filter(f => f.id !== file.id);
      }
      return [...prev, file];
    });
    onFileSelect && onFileSelect(file);
  }, [currentUser, onFileSelect]);

  const handleDelete = useCallback(async (file) => {
    if (!currentUser) return;
    try {
      const hasPermission = await checkPermission(currentUser, actualStorageArea, PERMISSIONS.DELETE);
      if (!hasPermission) {
        setError('You do not have permission to delete this file');
        return;
      }
      
      const storagePath = getStoragePath(
        currentUser.uid,
        currentUser.familyCode,
        collectionType,
        file.id,
        actualStorageArea
      );
      
      const storageRef = ref(storage, storagePath);
      
      try {
        await deleteObject(storageRef);
      } catch (storageError) {
        if (storageError.code === 'storage/object-not-found') {
          logger.warn(`File ${file.name} not found in storage, continuing with Firestore deletion`);
        } else {
          throw storageError;
        }
      }
      
      const collectionPath = getFirestorePath(currentUser.uid, currentUser.familyCode, collectionType, actualStorageArea);
      const fileRef = doc(db, collectionPath, file.id);
      await deleteDoc(fileRef);
      
      refresh();
    } catch (error) {
      logger.error('Error deleting file:', error);
      setError(error.message);
    }
  }, [currentUser, storage, actualStorageArea, collectionType, refresh, db]);

  const handleConfirmDelete = useCallback(async (files) => {
    if (!currentUser) return;
    try {
      const hasPermission = await checkPermission(currentUser, actualStorageArea, PERMISSIONS.DELETE);
      if (!hasPermission) {
        setError('You do not have permission to delete these files');
        return;
      }
      
      const collectionPath = getFirestorePath(currentUser.uid, currentUser.familyCode, collectionType, actualStorageArea);
      
      for (const file of files) {
        const fileRef = doc(db, collectionPath, file.id);
        const fileDoc = await getDoc(fileRef);
        
        if (fileDoc.exists()) {
          const recycleBinRef = collection(db, 'recycleBin');
          await addDoc(recycleBinRef, {
            userId: currentUser.uid,
            familyCode: currentUser.familyCode,
            originalPath: `${collectionPath}/${file.id}`,
            originalData: fileDoc.data(),
            name: file.name,
            deletedAt: new Date(),
            deletedBy: currentUser.uid,
            storageArea: actualStorageArea,
            collectionType: collectionType
          });
          
          await deleteDoc(fileRef);
        }
      }
      
      setDeleteDialogOpen(false);
      setItemsToDelete([]);
      onFileDelete && onFileDelete(files);
      refresh();
    } catch (error) {
      logger.error('Error moving files to recycle bin:', error);
    }
  }, [currentUser, collectionType, actualStorageArea, db, onFileDelete, refresh]);

  const handleShare = useCallback(async (file) => {
    if (!currentUser) return;
    try {
      const hasPermission = await checkPermission(currentUser, actualStorageArea, PERMISSIONS.SHARE);
      if (!hasPermission) {
        setError('You do not have permission to share this file');
        return;
      }
      setItemToShare(file);
      setShareDialogOpen(true);
    } catch (error) {
      logger.error('Error in share operation:', error);
      setError(error.message);
    }
  }, [currentUser, actualStorageArea]);

  // Open the Move dialog for cross-area moves
  const handleOpenMoveDialog = useCallback((file) => {
    if (!currentUser) return;
    setItemToMove(file);
    setOperationError(null);
    setMoveDialogOpen(true);
  }, [currentUser]);

  // Execute the cross-area move to the selected destination
  const handleCrossAreaMove = useCallback(async (targetArea) => {
    if (!currentUser || !itemToMove) return;
    try {
      setOperationLoading(true);
      setOperationError(null);

      const result = await moveFile({
        fileId: itemToMove.id,
        userId: currentUser.uid,
        familyCode: currentUser.familyCode,
        collectionType,
        sourceStorageArea: actualStorageArea,
        targetStorageArea: targetArea,
        targetPath: '/'
      });

      if (result) {
        setMoveDialogOpen(false);
        setItemToMove(null);
        onFileMove && onFileMove(itemToMove, targetArea);
        refresh();
      } else {
        setOperationError('Failed to move file. Please try again.');
      }
    } catch (error) {
      logger.error('Error moving file:', error);
      setOperationError(error.message);
    } finally {
      setOperationLoading(false);
    }
  }, [currentUser, itemToMove, collectionType, actualStorageArea, onFileMove, refresh]);

  // Open the Copy dialog
  const handleOpenCopyDialog = useCallback((file) => {
    if (!currentUser) return;
    setItemToCopy(file);
    setOperationError(null);
    setCopyDialogOpen(true);
  }, [currentUser]);

  // Execute the cross-area copy
  const handleCrossAreaCopy = useCallback(async (targetStorageArea) => {
    if (!currentUser || !itemToCopy) return;
    try {
      setOperationLoading(true);
      setOperationError(null);

      const result = await copyFile({
        fileId: itemToCopy.id,
        userId: currentUser.uid,
        familyCode: currentUser.familyCode,
        collectionType,
        sourceStorageArea: actualStorageArea,
        targetStorageArea,
        targetPath: '/'
      });

      if (result) {
        setCopyDialogOpen(false);
        setItemToCopy(null);
        refresh();
      } else {
        setOperationError('Failed to copy file. Please try again.');
      }
    } catch (error) {
      logger.error('Error copying file:', error);
      setOperationError(error.message);
    } finally {
      setOperationLoading(false);
    }
  }, [currentUser, itemToCopy, collectionType, actualStorageArea, refresh]);

  // eslint-disable-next-line no-unused-vars
  const handleMoveToVault = useCallback(async (file) => {
    if (!currentUser) return;
    try {
      setError(null);
      const result = await moveToVault({
        fileId: file.id,
        userId: currentUser.uid,
        familyCode: currentUser.familyCode,
        collectionType,
        isConfidential: true
      });

      if (result) {
        refresh();
      } else {
        throw new Error('Failed to move file to vault');
      }
    } catch (error) {
      logger.error('Error moving file to vault:', error);
      setError(error.message);
    }
  }, [currentUser, collectionType, refresh]);

  // eslint-disable-next-line no-unused-vars
  const handleMoveFromVault = useCallback(async (file) => {
    if (!currentUser) return;
    try {
      setError(null);
      const result = await moveFromVault({
        fileId: file.id,
        userId: currentUser.uid,
        familyCode: currentUser.familyCode,
        collectionType
      });

      if (result) {
        refresh();
      } else {
        throw new Error('Failed to move file from vault');
      }
    } catch (error) {
      logger.error('Error moving file from vault:', error);
      setError(error.message);
    }
  }, [currentUser, collectionType, refresh]);

  const handleUploadFiles = useCallback(async (files) => {
    if (!currentUser) return;
    try {
      const uploadPromises = files.map(async (file) => {
        const fileId = uuidv4();
        const storagePath = getStoragePath(
          currentUser.uid,
          currentUser.familyCode,
          collectionType,
          fileId,
          actualStorageArea
        );
        
        const storageRef = ref(storage, storagePath);
        await uploadBytesResumable(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        
        const collectionPath = getFirestorePath(
          currentUser.uid,
          currentUser.familyCode,
          collectionType,
          actualStorageArea
        );
        
        const fileRef = doc(db, collectionPath, fileId);
        await setDoc(fileRef, {
          name: file.name,
          type: file.type,
          size: file.size,
          path: currentPath,
          url: downloadURL,
          userId: currentUser.uid,
          familyCode: actualStorageArea === ACCESS_LEVELS.FAMILY ? currentUser.familyCode : null,
          createdAt: new Date(),
          updatedAt: new Date(),
          storageArea: actualStorageArea
        });
      });
      
      await Promise.all(uploadPromises);
      refresh();
    } catch (error) {
      logger.error('Error uploading files:', error);
      setError('Failed to upload files');
    }
  }, [currentUser, collectionType, actualStorageArea, storage, db, currentPath, refresh]);

  const handleUploadClose = useCallback(() => {
    setUploadDialogOpen(false);
    setFilesToUpload([]);
    refresh();
  }, [refresh]);

  const handleScanComplete = useCallback((result) => {
    logger.log('Scan completed:', result);
    setScannerDialogOpen(false);
    refresh();
  }, [refresh]);

  const handleVaultMenuOpen = useCallback((event, file) => {
    if (!currentUser) return;
    setSelectedForVault(file);
    setMenuAnchorEl(event.currentTarget);
  }, [currentUser]);

  const handleVaultMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedForVault(null);
  }, []);

  // Error handling effect
  useEffect(() => {
    if (vaultError) {
      setError(vaultError);
    }
  }, [vaultError]);

  // Folder initialization effect
  useEffect(() => {
    let isMounted = true;
    
    const initializeFolders = async () => {
      if (!currentUser?.uid) return;
      
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists() && !userDoc.data().foldersInitialized && isMounted) {
          await createInitialFolderStructure(currentUser.uid, currentUser.familyCode);
          await updateDoc(userRef, { foldersInitialized: true });
        }
      } catch (error) {
        logger.error('Error initializing folders:', error);
        if (isMounted) {
          setInitializationError('Failed to initialize folder structure');
        }
      }
    };

    initializeFolders();
    
    return () => {
      isMounted = false;
    };
  }, [currentUser?.uid, currentUser?.familyCode, db]);

  return (
    <Box className="flex flex-col h-full">
      <Box className="flex justify-between items-center mb-4">
        <Typography variant="h5" component="h2">{title}</Typography>
        <Box className="flex items-center gap-2">
          {!authLoading && currentUser && (
            <>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ScannerIcon />}
                onClick={() => setScannerDialogOpen(true)}
                disabled={itemsLoading || !!error}
                sx={{ mr: 1 }}
              >
                Scan
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setUploadDialogOpen(true)}
                disabled={itemsLoading || !!error}
              >
                Upload
              </Button>
              <IconButton
                onClick={() => setRecycleBinOpen(true)}
                title="Recycle Bin"
              >
                <RestoreFromTrash />
              </IconButton>
              <ViewModeToggle
                viewMode={viewMode}
                gridSize={gridSize}
                onViewModeChange={setViewMode}
                onGridSizeChange={setGridSize}
              />
            </>
          )}
        </Box>
      </Box>

      {authLoading ? (
        <Box className="flex justify-center items-center h-full">
          <CircularProgress />
        </Box>
      ) : !currentUser ? (
        <Alert severity="error">Please log in to view files</Alert>
      ) : error || initializationError || vaultError ? (
        <Alert severity="error">
          {error || initializationError || vaultError}
        </Alert>
      ) : itemsLoading ? (
        <Box className="flex justify-center items-center h-full">
          <CircularProgress />
        </Box>
      ) : items && items.length > 0 ? (
        <Box className="grid gap-4" style={{
          gridTemplateColumns: viewMode === 'grid' 
            ? `repeat(auto-fill, minmax(${
                gridSize === 'small' ? '150px' : 
                gridSize === 'medium' ? '200px' : '250px'
              }, 1fr))`
            : '1fr'
        }}>
          {items.map((item) => (
            <FileItem
              key={item.id}
              file={item}
              selected={selectedFiles.some(f => f.id === item.id)}
              viewMode={viewMode}
              gridSize={gridSize}
              onClick={handleFileSelect}
              onShare={handleShare}
              onCopy={handleOpenCopyDialog}
              onMove={handleOpenMoveDialog}
              onDelete={handleDelete}
              onVaultMenuOpen={(allowMoveToVault || allowMoveFromVault) ? handleVaultMenuOpen : undefined}
              allowMoveToVault={allowMoveToVault}
              allowMoveFromVault={allowMoveFromVault}
            />
          ))}
        </Box>
      ) : (
        <Box className="flex justify-center items-center h-full">
          <Typography color="textSecondary">
            No files found in this location
          </Typography>
        </Box>
      )}

      {/* Dialogs */}
      <UploadDialog
        open={uploadDialogOpen}
        onClose={handleUploadClose}
        selectedFiles={filesToUpload}
        currentPath={currentPath}
        collectionType={collectionType}
        storageArea={actualStorageArea}
        onUpload={handleUploadFiles}
      />
      
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        files={itemsToDelete}
      />

      <ShareDialog
        open={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
          setSelectedMembers([]);
          setShareRecursive(false);
          setInheritPermissions(true);
        }}
        item={itemToShare}
        selectedMembers={selectedMembers}
        onMemberSelect={setSelectedMembers}
        recursive={shareRecursive}
        onRecursiveChange={setShareRecursive}
        inheritPermissions={inheritPermissions}
        onInheritPermissionsChange={setInheritPermissions}
        onShare={handleShare}
        familyCode={currentUser?.familyCode}
        isVaultShare={actualStorageArea === ACCESS_LEVELS.VAULT}
      />
      
      <MoveDialog
        open={moveDialogOpen}
        onClose={() => {
          setMoveDialogOpen(false);
          setItemToMove(null);
          setOperationError(null);
        }}
        item={itemToMove}
        sourceArea={actualStorageArea}
        onMove={handleCrossAreaMove}
        actionType="move"
        loading={operationLoading}
        error={operationError}
      />

      <CopyDialog
        open={copyDialogOpen}
        onClose={() => {
          setCopyDialogOpen(false);
          setItemToCopy(null);
          setOperationError(null);
        }}
        item={itemToCopy}
        sourceArea={actualStorageArea}
        onCopy={handleCrossAreaCopy}
        loading={operationLoading}
        error={operationError}
      />

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleVaultMenuClose}
      >
        {/* Quick vault actions — move or copy to/from vault */}
        {allowMoveToVault && actualStorageArea !== ACCESS_LEVELS.VAULT && (
          <>
            <MenuItem onClick={() => {
              handleVaultMenuClose();
              setItemToCopy(selectedForVault);
              setOperationError(null);
              setCopyDialogOpen(true);
            }}>
              Copy to...
            </MenuItem>
            <MenuItem onClick={() => {
              handleVaultMenuClose();
              setItemToMove(selectedForVault);
              setOperationError(null);
              setMoveDialogOpen(true);
            }}>
              Move to...
            </MenuItem>
          </>
        )}
        {allowMoveFromVault && actualStorageArea === ACCESS_LEVELS.VAULT && (
          <>
            <MenuItem onClick={() => {
              handleVaultMenuClose();
              setItemToCopy(selectedForVault);
              setOperationError(null);
              setCopyDialogOpen(true);
            }}>
              Copy to...
            </MenuItem>
            <MenuItem onClick={() => {
              handleVaultMenuClose();
              setItemToMove(selectedForVault);
              setOperationError(null);
              setMoveDialogOpen(true);
            }}>
              Move to...
            </MenuItem>
          </>
        )}
      </Menu>

      <RecycleBin
        open={recycleBinOpen}
        onClose={() => {
          setRecycleBinOpen(false);
          refresh();
        }}
        userId={currentUser?.uid}
        familyCode={currentUser?.familyCode}
      />

      <DocumentScannerDialog
        open={scannerDialogOpen}
        onClose={() => setScannerDialogOpen(false)}
        onComplete={handleScanComplete}
        destinationPath={currentPath}
        destinationType={actualStorageArea === ACCESS_LEVELS.VAULT ? 'vault' : actualStorageArea === ACCESS_LEVELS.FAMILY ? 'family' : 'personal'}
      />
    </Box>
  );
};

export default FileExplorer; 